import { createSlice, current, original, PayloadAction } from '@reduxjs/toolkit'
import {
  DataTypePricePoint,
  DataTypeProduct,
  PricePointAttributes,
} from 'api-client'
import {
  DataTypeOrderLine,
  OrderLineResourceWithErrorMeta,
  OrderResponse,
  ProductResourceWithFakeRelationshipWithErrorMeta,
  ToSingleProductWithErrorMeta,
} from 'api-order-client'
import * as R from 'ramda'

import {
  BaseCartProduct,
  Cart,
  CartCustomerContext,
  CartProduct,
  ProductData,
} from '~shared-components/model'
import { OrderConfirmationProps } from '~shared-components/pages/OrderConfirmation'
import { saveCartState } from '~shared-components/redux/localStorage'
import { Price } from '~shared-components/transforms/price.transforms'
import { CustomerLocation } from '~shared-components/transforms/user.transforms'
import {
  ValidationErrorType,
  ValidationWarningType,
} from '~shared-components/translations/default'
import { canSumPrices, sumPrices } from '~shared-components/utils/price-utils'

export enum CartStatus {
  VALIDATING,
  VALID,
  INVALID,
  INITIALIZED,
}
export interface CartState {
  cartStatus: CartStatus
  carts: Cart[]
  loading: boolean
  orderConfirmations: Record<string, OrderConfirmationProps>
  populated: boolean
  validated: boolean
}

export const initialCartState: CartState = {
  cartStatus: CartStatus.INITIALIZED,
  carts: [],
  loading: true,
  orderConfirmations: {},
  populated: false,
  validated: false,
}

export interface CartAddressChange {
  customerContext: Cart['customerContext']
  location: CustomerLocation | undefined
}

export interface CartTotalValueChange {
  customerContext: Cart['customerContext']
  totalValue: Price | undefined
}

export interface RemoveProduct {
  customerContext: Cart['customerContext']
  productId: string
}

const sortProductsByName = (products: CartProduct[]) =>
  R.sortBy(product => product.data.name, products)

export const getCart = (
  id: string | undefined,
  carts: Cart[],
): Cart | undefined => {
  return id
    ? carts.find(c => c.customerContext?.customerContextId === id)
    : undefined
}

export const getCartIndex = (
  id: string | undefined,
  carts: Cart[],
): number | typeof NaN => {
  return Number(
    carts.findIndex(c => c.customerContext?.customerContextId === id),
  )
}

export const cartSlice = createSlice({
  initialState: initialCartState,
  name: 'cart',
  reducers: {
    billToAddressChangeAction(
      state,
      {
        payload: { customerContext, location },
      }: PayloadAction<CartAddressChange>,
    ) {
      const cartIndex = getCartIndex(
        customerContext.customerContextId,
        state.carts as Cart[],
      )
      if (!(cartIndex >= 0)) return

      state.cartStatus = CartStatus.INITIALIZED
      state.carts[cartIndex].billToLocation = location
    },

    emptyCartAction: () => initialCartState,

    initCartAction(state) {
      state.cartStatus = CartStatus.INITIALIZED
      state.populated = false
    },

    initCartValidationAction(state) {
      state.cartStatus = CartStatus.VALIDATING
      state.loading = true
    },

    orderFailure(state) {
      state.loading = false
    },

    orderSuccess: {
      prepare(payload: OrderConfirmationProps, meta: string) {
        return { meta, payload }
      },
      reducer(
        state,
        {
          meta: userId,
          payload,
        }: PayloadAction<OrderConfirmationProps, string, string>,
      ) {
        state.cartStatus = CartStatus.INITIALIZED
        state.carts = []
        state.loading = false
        state.orderConfirmations[payload.orderNumber] = payload
        saveCartState(userId, current(state) as CartState)
      },
    },

    populateCartContentsAction: (
      state,
      { payload }: PayloadAction<Partial<CartState>>,
    ) => ({
      ...state,
      ...payload,
      cartStatus: CartStatus.INITIALIZED,
      carts:
        (payload?.carts &&
          payload.carts.map(c => ({ ...c, isValid: undefined }))) ||
        [],
      loading: false,
      populated: true,
    }),

    removeFromCartAction: {
      prepare(payload: RemoveProduct, meta: string) {
        return { meta, payload }
      },
      reducer(
        state,
        {
          meta: userId,
          payload: { customerContext, productId },
        }: PayloadAction<RemoveProduct, string, string>,
      ) {
        const cartIndex = getCartIndex(
          customerContext.customerContextId,
          state.carts as Cart[],
        )
        if (!(cartIndex >= 0)) return

        const cart = original(state.carts[cartIndex]) as Cart
        const products = cart.products.filter(p => p.data.id !== productId)
        const allPrices = products.map(({ price }) => price)
        const totalValue = canSumPrices(allPrices)
          ? sumPrices(allPrices)
          : undefined
        const isValid =
          cart.isValid &&
          (products.length > 0 ? totalValue !== undefined : true)

        // build immer state
        state.cartStatus = isValid ? CartStatus.INITIALIZED : CartStatus.INVALID

        if (products.length > 0) {
          state.carts.splice(cartIndex, 1, {
            ...cart,
            isValid,
            products,
            totalValue,
          })
        } else {
          state.carts.splice(cartIndex, 1)
        }

        saveCartState(userId, current(state) as CartState)
      },
    },

    shipToAddressChangeAction(
      state,
      {
        payload: { customerContext, location },
      }: PayloadAction<CartAddressChange>,
    ) {
      const cartIndex = getCartIndex(
        customerContext.customerContextId,
        state.carts as Cart[],
      )
      if (!(cartIndex >= 0)) return

      state.cartStatus = CartStatus.INITIALIZED
      state.carts[cartIndex].shipToLocation = location
    },

    totalValueChangeAction(
      state,
      {
        payload: { customerContext, totalValue },
      }: PayloadAction<CartTotalValueChange>,
    ) {
      const cartIndex = getCartIndex(
        customerContext.customerContextId,
        state.carts as Cart[],
      )
      if (!(cartIndex >= 0)) return

      state.cartStatus = CartStatus.INITIALIZED
      state.carts[cartIndex].totalValue = totalValue
    },

    updateCartFieldAction(
      state,
      {
        payload: { customerContext, key, value },
      }: PayloadAction<{
        customerContext: Cart['customerContext']
        key: keyof Cart
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any
      }>,
    ) {
      const cartIndex = getCartIndex(
        customerContext.customerContextId,
        state.carts as Cart[],
      )
      if (!(cartIndex >= 0)) return

      const cart = state.carts[cartIndex]
      const isValidationRequiredForKey = (key: string) =>
        ['deliveryDate'].includes(key)

      if (isValidationRequiredForKey(key)) {
        state.cartStatus = CartStatus.INITIALIZED
      }

      state.carts.splice(cartIndex, 1, {
        ...cart,
        [key]: value,
      })
      state.loading = false
    },

    updateCartStatus(
      state,
      {
        payload: { loading, status },
      }: PayloadAction<{
        loading?: boolean
        status?: CartStatus
      }>,
    ) {
      if (loading !== undefined) state.loading = loading
      if (status !== undefined) state.cartStatus = status
    },

    updateCartWithValidation: {
      prepare(
        payload: {
          currentCart: Cart
          customerContextId: string
          validationResponse?: OrderResponse
        },
        meta: string,
      ) {
        return { meta, payload }
      },
      reducer(
        state,
        {
          meta: userId,
          payload: { currentCart, customerContextId, validationResponse },
        }: PayloadAction<
          {
            currentCart: Cart
            customerContextId: string
            validationResponse?: OrderResponse
          },
          string,
          string
        >,
      ) {
        const platformId =
          validationResponse?.meta.platformId || currentCart.platformId
        const validatedProducts = validationResponse?.included
          ? (validationResponse.included
              .filter(included => included.type === DataTypeOrderLine.OrderLine)
              .map(
                orderLine =>
                  orderLine.type === DataTypeOrderLine.OrderLine &&
                  orderLine.relationships.product,
              ) as ToSingleProductWithErrorMeta[])
          : []

        const validatedOrderLines = validationResponse?.included?.filter(
          included =>
            included.type === DataTypeOrderLine.OrderLine && included.meta,
        ) as OrderLineResourceWithErrorMeta[]

        const products = currentCart.products.map(p => {
          let { price, pricePoints } = p
          const errors: ValidationErrorType[] = []
          const warnings: ValidationWarningType[] = []
          const generalValidations = validatedProducts.find(
            v => v.data.id === p.data.id && v.meta,
          )
          const priceValidation = validatedOrderLines?.find(
            ol =>
              ol.type === DataTypeOrderLine.OrderLine &&
              ol.relationships.product.data.id === p.data.id,
          )

          if (generalValidations && generalValidations.meta?.error) {
            const err = generalValidations.meta.error
            errors.push(err)
            if (err == ValidationErrorType.PriceNotFound) {
              price = undefined
              pricePoints = []
            }
          }

          if (priceValidation) {
            if (priceValidation.attributes.pricingUnit) {
              warnings.push(ValidationWarningType.PriceChanged)
              const productResource = validationResponse?.included?.find(
                product =>
                  product.type == DataTypeProduct.Product &&
                  product.id == p.data.id,
              ) as ProductResourceWithFakeRelationshipWithErrorMeta
              const pricePointsKeys = new Set(
                productResource?.relationships.pricePoints?.data.map(
                  pp => pp.id,
                ),
              )
              pricePoints = validationResponse?.included
                ?.filter(
                  included =>
                    included.type == DataTypePricePoint.PricePoint &&
                    pricePointsKeys.has(included.id),
                )
                .map(pp => pp.attributes) as PricePointAttributes[]
              price = Price.fromMonetaryAmount(
                priceValidation.attributes.subtotalValue,
              )
            } else {
              errors.push(ValidationErrorType.PriceNotFound)
              price = undefined
              pricePoints = []
            }
          }

          return {
            ...p,
            errors,
            price,
            pricePoints,
            warnings,
          }
        })

        const allPrices = products.map(({ price }) => price)
        const totalValue = canSumPrices(allPrices)
          ? sumPrices(allPrices)
          : undefined
        const isValid =
          validatedProducts.length > 0 &&
          validatedProducts.every(p => p.meta === undefined) &&
          products.every(p => p.errors.length === 0) &&
          (products.length > 0 ? totalValue !== undefined : true)

        // build immer state
        state.cartStatus = isValid ? CartStatus.VALID : CartStatus.INVALID
        const cartIndex = getCartIndex(customerContextId, state.carts as Cart[])

        state.carts.splice(cartIndex, 1, {
          ...currentCart,
          isValid,
          platformId,
          products,
          totalValue,
        })
        state.loading = false

        saveCartState(userId, current(state) as CartState)
      },
    },

    updateProductQuantityAction(
      state,
      {
        payload: {
          location,
          product: {
            customerContext: untrimmedCustomerContext,
            data: untrimmedData,
            orderAmount,
            price,
            pricePoints,
            quantity,
          },
        },
      }: PayloadAction<{
        location: CustomerLocation | undefined
        product: BaseCartProduct
      }>,
    ) {
      if (untrimmedCustomerContext === undefined) return

      // Trim the customer context to save only the necessary fields to local storage
      const customerContext: CartCustomerContext = R.pick(
        ['customerContextId', 'customerNumber'],
        untrimmedCustomerContext,
      )

      const productDataKeys: (keyof ProductData)[] = [
        'name',
        'id',
        'isDangerous',
        'isHazardous',
        'sourceId',
        'packaging',
        'customerProductId',
      ]

      const data = R.pick(productDataKeys, untrimmedData)
      const cartIndex = getCartIndex(
        customerContext.customerContextId,
        state.carts as Cart[],
      )

      const productToAdd =
        quantity && quantity > 0
          ? [
              {
                customerContext,
                data,
                orderAmount,
                price,
                pricePoints,
                quantity,
              },
            ]
          : []

      if (cartIndex >= 0) {
        const cart = state.carts[cartIndex]
        const newProducts = cart.products
          .filter(product => product.data.id !== data.id)
          .concat(productToAdd)
        const products = sortProductsByName(newProducts as CartProduct[])

        const allPrices = products.map(({ price }) => price)
        const totalValue = canSumPrices(allPrices)
          ? sumPrices(allPrices)
          : undefined
        const isValid =
          cart.isValid &&
          (products.length > 0 ? totalValue !== undefined : true)

        // build immer state
        state.cartStatus = isValid ? CartStatus.INITIALIZED : CartStatus.INVALID
        state.carts.splice(cartIndex, 1, {
          ...cart,
          isValid,
          products,
          totalValue,
        })
        state.loading = false
      } else {
        const allPrices = productToAdd.map(({ price }) => price)
        const totalValue = canSumPrices(allPrices)
          ? sumPrices(allPrices)
          : undefined
        const isValid =
          productToAdd.length > 0 ? totalValue !== undefined : true

        //build immer state
        state.cartStatus = isValid ? CartStatus.INITIALIZED : CartStatus.INVALID
        state.carts.push({
          customerContext,
          isValid,
          products: productToAdd,
          shipToLocation: location,
          totalValue,
        })
        state.loading = false
      }
    },
  },
})

export default cartSlice.reducer
